
import {
  IResourceComponentsProps,
  useTranslate,
  BaseKey,
} from '@pankod/refine-core';
import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Checkbox,
  Select,
  Button,
  Modal,
  Upload,
  Alert,
  message,
  DatePicker,
} from 'antd';
import type { FormInstance, UploadFile } from 'antd';
import type { UploadProps } from 'antd/es/upload';
import type { RcFile } from 'antd/es/upload/interface';
import { Ownership, User } from 'interfaces';
import UserHandler from 'util/UserHandler';
import { OwnershipType } from 'types/ownership/ownershipType';
import { OwnershipAttachmentType } from 'types/ownership/ownershipAttachmentType';
import { AttachmentValidationStatus } from 'types/ownership/attachmentValidationStatus';
import { UserRoleEnum } from 'types/users/userRole';
import axios from 'axios';
import { CustomUploadFile } from 'types/ownership/customUploadFile';
import { EnumToArray } from 'util/parseData';
import dayjs from 'dayjs';

interface OwnershipFormProps {
  form: FormInstance<Ownership>;
  ownershipType: OwnershipType | undefined;
  setOwnershipType: React.Dispatch<React.SetStateAction<OwnershipType | undefined>>;
  fileList: CustomUploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<CustomUploadFile[]>>;
  country: { value: BaseKey; label: string }[];
  setCountry: React.Dispatch<React.SetStateAction<{ value: BaseKey; label: string }[]>>;
  states: { value: BaseKey; label: string }[];
  setStates: React.Dispatch<React.SetStateAction<{ value: BaseKey; label: string }[]>>;
  countrySel: string;
  setCountrySel: React.Dispatch<React.SetStateAction<string>>;
  stateSel: string;
  setStateSel: React.Dispatch<React.SetStateAction<string>>;
  users: { value: BaseKey; label: string }[];
  selectPropsUser: any;
  onSearchUser: (searchText: string) => void;
  onSearchCountry: (searchText: string) => void;
  onSearchState: (searchText: string) => void;
  newUserVisible: boolean;
  setNewUserVisible: React.Dispatch<React.SetStateAction<boolean>>;
  newUserError: boolean;
  setNewUserError: React.Dispatch<React.SetStateAction<boolean>>;
  newUserPhone?: string;
  setNewUserPhone: React.Dispatch<React.SetStateAction<string | undefined>>;
  newUserEmail?: string;
  setNewUserEmail: React.Dispatch<React.SetStateAction<string | undefined>>;
  queryResultUser: any;
  typeOptions: { value: string; label: string }[];
  authUser: User;
  tabItems: any;
  apiUrl: string;
  onFinish: () => Promise<void>;
}

const beforeUpload = (file: RcFile) => {
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
  ];
  const isValidType = allowedTypes.includes(file.type);

  if (!isValidType) {
    message.error('Format non supporté !');
    return Upload.LIST_IGNORE;
  }

  const isLt5M = file.size / 1024 / 1024 < 5;
  if (!isLt5M) {
    message.error('Le fichier doit être inférieur à 5 Mo !');
  }

  return isValidType && isLt5M;
};

export const OwnershipForm: React.FC<OwnershipFormProps> = ({
  form,
  ownershipType,
  setOwnershipType,
  fileList,
  setFileList,
  country,
  setCountry,
  states,
  setStates,
  countrySel,
  setCountrySel,
  stateSel,
  setStateSel,
  users,
  selectPropsUser,
  onSearchUser,
  onSearchCountry,
  onSearchState,
  newUserVisible,
  setNewUserVisible,
  newUserError,
  setNewUserError,
  newUserPhone,
  setNewUserPhone,
  newUserEmail,
  setNewUserEmail,
  queryResultUser,
  typeOptions,
  authUser,
  tabItems,
  apiUrl,
  onFinish,
}) => {
  const t = useTranslate();
  const [userForm] = Form.useForm();
  const { TextArea } = Input;

  // Déplacer validationStatusOptions à l'intérieur du composant
  const validationStatusOptions = EnumToArray(AttachmentValidationStatus).map((op) => ({
    value: op.value,
    label: t(`status.${op.label}`, { ns: 'ownerships' }), // Assurez-vous que les traductions existent
  }));

  const attachmentTypeOptions = EnumToArray(OwnershipAttachmentType).map((op) => ({
    value: op.value,
    label: t(`types.${op.label}`, { ns: 'attachments' }),
  }));

  const attachmentStatusOptions = EnumToArray(AttachmentValidationStatus).map((op) => ({
    value: op.value,
    label: t(`status.${op.label}`, { ns: 'attachments' }),
  }));

  // Synchronize form fields with fileList for attachment display
  useEffect(() => {
    const currentAttachments = form.getFieldValue('attachments') || [];
    const updatedAttachments = fileList.map((file, index) => ({
      path: file.path || '',
      ownership_type: file.ownership_type || OwnershipAttachmentType.Certificat_de_propriete,
      custom_type: file.custom_type || null,
      details: file.details || '',
      document_number: file.document_number || '',
      issue_date: file.issue_date || null,
      expiration_date: file.expiration_date || null,
      validation_status: file.validation_status || AttachmentValidationStatus.waiting,
      observations: file.observations || '',
    }));
    if (JSON.stringify(currentAttachments) !== JSON.stringify(updatedAttachments)) {
      form.setFieldsValue({ attachments: updatedAttachments });
    }
  }, [fileList, form]);

  const handleChange =
    (index: number) =>
    ({ fileList: newFileList }: UploadProps) => {
      if (!newFileList) {
        return;
      }

      const updatedFileList: CustomUploadFile[] = newFileList.map((file) => {
        if (file.response && file.response.filePath) {
          return {
            ...file,
            url: process.env.REACT_APP_SERVER + file.response.filePath,
            path: file.response.filePath,
            status: 'done' as const,
            ownership_type: OwnershipAttachmentType.Certificat_de_propriete,
            custom_type: null,
            details: '',
            document_number: '',
            issue_date: null,
            expiration_date: null,
            validation_status: AttachmentValidationStatus.waiting,
            observations: '',
          } as CustomUploadFile;
        }
        return {
          ...file,
          ownership_type: OwnershipAttachmentType.Certificat_de_propriete,
          custom_type: null,
          details: '',
          document_number: '',
          issue_date: null,
          expiration_date: null,
          validation_status: AttachmentValidationStatus.waiting,
          observations: '',
        } as CustomUploadFile;
      });

      const currentAttachments = form.getFieldValue('attachments') || [];
      if (updatedFileList[0]) {
        currentAttachments[index] = {
          ...currentAttachments[index],
          path: updatedFileList[0].path || '',
          ownership_type: updatedFileList[0].ownership_type || OwnershipAttachmentType.Certificat_de_propriete,
          custom_type: updatedFileList[0].custom_type || null,
          details: updatedFileList[0].details,
          document_number: updatedFileList[0].document_number,
          issue_date: updatedFileList[0].issue_date,
          expiration_date: updatedFileList[0].expiration_date,
          validation_status: updatedFileList[0].validation_status || AttachmentValidationStatus.waiting,
          observations: updatedFileList[0].observations,
        };
      }
      form.setFieldsValue({ attachments: currentAttachments });
      setFileList((prev) => {
        const newAttachments = [...prev];
        newAttachments[index] = updatedFileList[0] || prev[index];
        return newAttachments;
      });
    };

  const handleRemove = async (file: UploadFile | CustomUploadFile, index?: number) => {
    try {
      const customFile: CustomUploadFile = {
        ...file,
        path: file.response?.filePath || (file as CustomUploadFile).path || '',
      };

      const ownershipId = form.getFieldValue('id');
      if (!ownershipId) {
        message.error("ID de l'ownership non disponible.");
        return false;
      }

      if (!customFile.path) {
        message.error('Chemin du fichier non disponible.');
        return false;
      }

      const response = await axios.delete(`${apiUrl}/attachments/by-path`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${UserHandler.getInstance().token}`,
        },
        data: {
          path: customFile.path,
          ownership_id: ownershipId,
        },
      });

      if (response.status === 200) {
        if (index !== undefined) {
          setFileList((prev) => {
            const newAttachments = [...prev];
            newAttachments.splice(index, 1);
            return newAttachments;
          });
          const currentAttachments = form.getFieldValue('attachments') || [];
          currentAttachments.splice(index, 1);
          form.setFieldsValue({ attachments: currentAttachments });
        }
        message.success('Fichier supprimé avec succès !');
        return true;
      } else {
        message.error('Échec de la suppression du fichier.');
        return false;
      }
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      message.error('Erreur lors de la suppression du fichier.');
      return false;
    }
  };

  const handleOk = async () => {
    setNewUserError(false);
    try {
      await userForm.validateFields();
      const userFormData = Object.assign(userForm.getFieldsValue(), {
        password: Math.random().toString(36).slice(-8),
        newsletter: false,
        role: UserRoleEnum.User,
        parent_user_id: authUser?.id,
      });
      if (!userFormData.phone) delete userFormData.phone;
      if (!userFormData.email) delete userFormData.email;

      const response = await axios
        .post(`${apiUrl}/user`, userFormData, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${UserHandler.getInstance().token}`,
          },
        })
        .then((res) => res.data)
        .catch((err) => err.response);

      if (response?.data?.id) {
        userFormData.id = response?.data?.id;
        form.setFieldsValue({ user_id: response?.data?.id });
        queryResultUser?.refetch();
        setNewUserVisible(false);
        message.success('Utilisateur créé avec succès !');
      } else {
        setNewUserError(true);
      }
    } catch (error) {
      setNewUserError(true);
      message.error("Erreur lors de la création de l'utilisateur.");
    }
  };

  const handleCancel = () => {
    userForm.resetFields();
    setNewUserVisible(false);
  };

  return (
    <div style={{ padding: '16px', background: '#f5f5f5' }}>
      <Form form={form} name="ownership_form" layout="vertical">
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={6} lg={4} xl={4}>
            <Form.Item name="published" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.published', { ns: 'ownerships' })}</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6} lg={4} xl={4}>
            <Form.Item name="loti" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.loti', { ns: 'ownerships' })}</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6} lg={4} xl={4}>
            <Form.Item name="electricity" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.electricity', { ns: 'ownerships' })}</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8} md={6} lg={4} xl={4}>
            <Form.Item name="water" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.water', { ns: 'ownerships' })}</Checkbox>
            </Form.Item>
          </Col>
          {ownershipType && ownershipType !== OwnershipType.Ground && (
            <>
              <Col xs={12} sm={8} md={6} lg={4} xl={4}>
                <Form.Item name="parking" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.parking', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4}>
                <Form.Item name="elevator" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.elevator', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4}>
                <Form.Item name="corridor" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.corridor', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4}>
                <Form.Item name="concierge" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.concierge', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4}>
                <Form.Item name="dogs_allowed" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.dogs_allowed', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4}>
                <Form.Item name="kitchen" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.kitchen', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
            </>
          )}
          {ownershipType === OwnershipType.Hotel && (
            <>
              <Col xs={12} sm={8} md={6} lg={4} xl={4}>
                <Form.Item name="wi_fi" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.wi_fi', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4}>
                <Form.Item name="safe_box" valuePropName="checked" noStyle>
                  <Checkbox>{t('fields.safe_box', { ns: 'ownerships' })}</Checkbox>
                </Form.Item>
              </Col>
            </>
          )}
          <Col span={24} style={{ marginTop: '8px' }}>
            <Form.List name="attachments">
              {(fields, { add, remove }) => (
                <div style={{ padding: '16px', background: '#fff', border: '1px solid #e8e8e8' }}>
                  {fields.map(({ key, name, ...restField }, index) => {
                    const attachmentType = form.getFieldValue(['attachments', name, 'ownership_type']);
                    const isAutre = attachmentType === OwnershipAttachmentType.Autre;
                    const customTypeValue = form.getFieldValue(['attachments', name, 'custom_type']);

                    return (
                      <Row
                        key={key}
                        gutter={[16, 16]}
                        style={{ marginBottom: '16px', borderBottom: '1px solid #e8e8e8', paddingBottom: '16px' }}
                      >
                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            label={t('fields.attachment_file', { ns: 'attachments' })}
                            name={[name, 'path']}
                            rules={[{ required: true, message: 'Veuillez uploader un fichier' }]}
                          >
                            <Upload
                              action={`${apiUrl}/media/upload`}
                              data={{ table: `ownerships/${form.getFieldValue('id')}` }}
                              listType="text"
                              accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/gif,image/webp"
                              onChange={handleChange(index)}
                              onRemove={(file) => handleRemove(file, index)}
                              fileList={fileList[index] ? [fileList[index]] : []}
                              maxCount={1}
                              beforeUpload={beforeUpload}
                            >
                              {(!fileList[index] || fileList[index].status !== 'done') && (
                                <Button style={{ borderRadius: '6px' }}>{t('Upload')}</Button>
                              )}
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            label={t('type', { ns: 'attachments' })}
                            name={[name, 'ownership_type']}
                            rules={[{ required: true, message: 'Veuillez sélectionner un type' }]}
                          >
                            <Select
                              options={attachmentTypeOptions}
                              onChange={(value) => {
                                const currentAttachments = form.getFieldValue('attachments') || [];
                                currentAttachments[index] = {
                                  ...currentAttachments[index],
                                  ownership_type: value,
                                  custom_type: value !== OwnershipAttachmentType.Autre ? null : currentAttachments[index].custom_type,
                                };
                                form.setFieldsValue({ attachments: currentAttachments });
                              }}
                            />
                          </Form.Item>
                        </Col>
                        {isAutre && (
                          <Col xs={24} sm={12}>
                            <Form.Item
                              {...restField}
                              label={t('fields.custom_type', { ns: 'attachments' })}
                              name={[name, 'custom_type']}
                              rules={[{ required: true, message: 'Veuillez entrer un type personnalisé pour "Autres"' }]}
                              initialValue={customTypeValue}
                            >
                              <Input maxLength={255} />
                            </Form.Item>
                          </Col>
                        )}
                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            label={t('fields.details', { ns: 'attachments' })}
                            name={[name, 'details']}
                          >
                            <TextArea maxLength={500} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'document_number']}
                            label={t('fields.document_number', { ns: 'attachments' })}
                          >
                            <Input maxLength={255} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            label={t('fields.issue_date', { ns: 'attachments' })}
                            name={[name, 'issue_date']}
                            getValueProps={(value: any) => ({
                              value: value ? dayjs(value) : null,
                            })}
                          >
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            label={t('fields.expiration_date', { ns: 'attachments' })}
                            name={[name, 'expiration_date']}
                            getValueProps={(value: any) => ({
                              value: value ? dayjs(value) : null,
                            })}
                          >
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        {authUser.role === UserRoleEnum.Admin && (
                          <Col xs={24} sm={12}>
                            <Form.Item
                              {...restField}
                              label={t('fields.validation_status', { ns: 'attachments' })}
                              name={[name, 'validation_status']}
                              rules={[{ required: true, message: 'Veuillez sélectionner un statut de validation' }]}
                            >
                              <Select options={attachmentStatusOptions} />
                            </Form.Item>
                          </Col>
                        )}
                        <Col xs={24}>
                          <Form.Item
                            {...restField}
                            label={t('fields.observations', { ns: 'attachments' })}
                            name={[name, 'observations']}
                          >
                            <TextArea maxLength={1000} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} style={{ textAlign: 'right' }}>
                          <Button danger onClick={() => remove(name)}>
                            {t('Remove Attachment')}
                          </Button>
                        </Col>
                      </Row>
                    );
                  })}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() =>
                        add({
                          ownership_type: OwnershipAttachmentType.Certificat_de_propriete,
                          validation_status: AttachmentValidationStatus.waiting,
                          custom_type: null,
                        })
                      }
                      block
                      icon={<i className="anticon anticon-plus" />}
                      disabled={fields.length >= 5}
                    >
                      {t('Add Attachment')}
                    </Button>
                  </Form.Item>
                </div>
              )}
            </Form.List>
          </Col>
          <Col xs={24} sm={24} md={authUser.role === UserRoleEnum.Admin || authUser.role === UserRoleEnum.Agency ? 8 : 12} lg={authUser.role === UserRoleEnum.Admin || authUser.role === UserRoleEnum.Agency ? 8 : 12} xl={authUser.role === UserRoleEnum.Admin || authUser.role === UserRoleEnum.Agency ? 8 : 12}>
            <Form.Item
              label={t('fields.name', { ns: 'ownerships' })}
              name="name"
              rules={[{ required: true, message: `Veuillez entrer ${t('fields.name', { ns: 'ownerships' })}` }]}
            >
              <Input disabled={tabItems.conveyances.dataSource.length > 0} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={authUser.role === UserRoleEnum.Admin || authUser.role === UserRoleEnum.Agency ? 8 : 12} lg={authUser.role === UserRoleEnum.Admin || authUser.role === UserRoleEnum.Agency ? 8 : 12} xl={authUser.role === UserRoleEnum.Admin || authUser.role === UserRoleEnum.Agency ? 8 : 12}>
            <Form.Item
              label={t('fields.type', { ns: 'ownerships' })}
              name="type"
              rules={[{ required: true, message: `Veuillez sélectionner ${t('fields.type', { ns: 'ownerships' })}` }]}
            >
              <Select options={typeOptions} onChange={(value) => setOwnershipType(value)} disabled />
            </Form.Item>
          </Col>
          {authUser.role === UserRoleEnum.Admin && (
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item
                label={t('fields.validation_status', { ns: 'ownerships' })}
                name="validation_status"
                rules={[{ required: true, message: `Veuillez sélectionner ${t('fields.validation_status', { ns: 'ownerships' })}` }]}
              >
                <Select options={validationStatusOptions} />
              </Form.Item>
            </Col>
          )}
          {authUser.role === UserRoleEnum.Admin || authUser.role === UserRoleEnum.Agency ? (
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item
                label={t('fields.user_id', { ns: 'ownerships' })}
                rules={[{ required: true, message: `Veuillez sélectionner ${t('fields.user_id', { ns: 'ownerships' })}` }]}
                shouldUpdate={(prevValues, curValues) => prevValues.user_id !== curValues.user_id}
              >
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Form.Item
                    name="user_id"
                    rules={[{ required: true, message: `Veuillez sélectionner ${t('fields.user_id', { ns: 'ownerships' })}` }]}
                    style={{ flex: 1, marginBottom: 0 }}
                  >
                    <Select options={users} onSearch={onSearchUser} disabled={tabItems.conveyances.dataSource.length > 0} />
                  </Form.Item>
                  <Button
                    style={{ width: '100px' }}
                    onClick={() => setNewUserVisible(true)}
                    disabled={tabItems.conveyances.dataSource.length > 0}
                  >
                    {t('fields.new', { ns: 'ownerships' })}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          ) : (
            <Form.Item name="user_id" hidden>
              <InputNumber hidden />
            </Form.Item>
          )}
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item
              label={t('fields.short_description', { ns: 'ownerships' })}
              name="short_description"
              rules={[{ required: true, message: `Veuillez entrer ${t('fields.short_description', { ns: 'ownerships' })}` }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item
              label={t('fields.price', { ns: 'ownerships' })}
              name="price"
              rules={[{ required: true, message: `Veuillez entrer ${t('fields.price', { ns: 'ownerships' })}` }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item
              label={t('fields.state', { ns: 'ownerships' })}
              name="state"
              rules={[{ required: true, message: `Veuillez sélectionner ${t('fields.state', { ns: 'ownerships' })}` }]}
            >
              <Select
                options={country}
                showSearch
                onSearch={onSearchCountry}
                onChange={(value) => setCountrySel(value)}
                disabled={tabItems.conveyances.dataSource.length > 0}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item
              label={t('fields.province', { ns: 'ownerships' })}
              name="province"
              rules={[{ required: true, message: `Veuillez sélectionner ${t('fields.province', { ns: 'ownerships' })}` }]}
            >
              <Select
                options={states}
                showSearch
                onSearch={onSearchState}
                onChange={(value) => setStateSel(value)}
                disabled={tabItems.conveyances.dataSource.length > 0}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item
              label={t('fields.city', { ns: 'ownerships' })}
              name="city"
              rules={[{ required: true, message: `Veuillez entrer ${t('fields.city', { ns: 'ownerships' })}` }]}
            >
              <Input disabled={tabItems.conveyances.dataSource.length > 0} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item
              label={t('fields.address', { ns: 'ownerships' })}
              name="address"
              rules={[{ required: true, message: `Veuillez entrer ${t('fields.address', { ns: 'ownerships' })}` }]}
            >
              <Input disabled={tabItems.conveyances.dataSource.length > 0} />
            </Form.Item>
          </Col>
          {authUser.role === UserRoleEnum.Admin ? (
            <>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item label={t('fields.latitude', { ns: 'ownerships' })} name="latitude">
                  <InputNumber style={{ width: '100%' }} disabled={tabItems.conveyances.dataSource.length > 0} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item label={t('fields.longitude', { ns: 'ownerships' })} name="longitude">
                  <InputNumber style={{ width: '100%' }} disabled={tabItems.conveyances.dataSource.length > 0} />
                </Form.Item>
              </Col>
            </>
          ) : (
            <>
              <Form.Item name="latitude" hidden>
                <InputNumber hidden />
              </Form.Item>
              <Form.Item name="longitude" hidden>
                <InputNumber hidden />
              </Form.Item>
            </>
          )}
          {ownershipType && ownershipType !== OwnershipType.Ground && (
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item label={t('fields.internal', { ns: 'ownerships' })} name="internal">
                <Input disabled={tabItems.conveyances.dataSource.length > 0} />
              </Form.Item>
            </Col>
          )}
          {ownershipType && (
            <>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label={t('fields.surface', { ns: 'ownerships' })}
                  name="surface"
                  rules={[{ required: true, message: `Veuillez entrer ${t('fields.surface', { ns: 'ownerships' })}` }]}
                >
                  <InputNumber style={{ width: '100%' }} disabled={tabItems.conveyances.dataSource.length > 0} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label={t('fields.availables', { ns: 'ownerships' })}
                  name="availables"
                  rules={[{ required: true, message: `Veuillez entrer ${t('fields.availables', { ns: 'ownerships' })}` }]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </>
          )}
          {ownershipType && ownershipType !== OwnershipType.Ground && (
            <>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label={t('fields.living_rooms', { ns: 'ownerships' })}
                  name="living_rooms"
                  rules={[{ required: true, message: `Veuillez entrer ${t('fields.living_rooms', { ns: 'ownerships' })}` }]}
                >
                  <InputNumber style={{ width: '100%' }} disabled={tabItems.conveyances.dataSource.length > 0} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item label={t('fields.dining_rooms', { ns: 'ownerships' })} name="dining_rooms">
                  <InputNumber style={{ width: '100%' }} disabled={tabItems.conveyances.dataSource.length > 0} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label={t('fields.rooms', { ns: 'ownerships' })}
                  name="rooms"
                  rules={[{ required: true, message: `Veuillez entrer ${t('fields.rooms', { ns: 'ownerships' })}` }]}
                >
                  <InputNumber style={{ width: '100%' }} disabled={tabItems.conveyances.dataSource.length > 0} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item
                  label={t('fields.bathrooms', { ns: 'ownerships' })}
                  name="bathrooms"
                  rules={[{ required: true, message: `Veuillez entrer ${t('fields.bathrooms', { ns: 'ownerships' })}` }]}
                >
                  <InputNumber style={{ width: '100%' }} disabled={tabItems.conveyances.dataSource.length > 0} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item label={t('fields.floors', { ns: 'ownerships' })} name="floors">
                  <InputNumber style={{ width: '100%' }} disabled={tabItems.conveyances.dataSource.length > 0} />
                </Form.Item>
              </Col>
            </>
          )}
          <Col span={24}>
            <Form.Item label={t('fields.description', { ns: 'ownerships' })} name="description">
              <TextArea rows={4} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Modal
        title={t('fields.new_user', { ns: 'ownerships' })}
        visible={newUserVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={t('save')}
        cancelText={t('cancel')}
      >
        {newUserError && (
          <Alert message={t('fields.user_error', { ns: 'ownerships' })} type="error" showIcon closable style={{ marginBottom: '16px' }} />
        )}
        <Form form={userForm} layout="vertical">
          <Form.Item
            label={t('fields.firstname', { ns: 'users' })}
            name="firstname"
            rules={[{ required: true, message: `Veuillez entrer ${t('fields.firstname', { ns: 'users' })}` }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('fields.lastname', { ns: 'users' })}
            name="lastname"
            rules={[{ required: true, message: `Veuillez entrer ${t('fields.lastname', { ns: 'users' })}` }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('fields.phone', { ns: 'users' })}
            name="phone"
            rules={[{ pattern: /^\+?[1-9]\d{1,14}$/, message: `Veuillez entrer un ${t('fields.phone', { ns: 'users' })} valide` }]}
          >
            <Input onChange={(e) => setNewUserPhone(e.target.value)} />
          </Form.Item>
          <Form.Item
            label={t('fields.email', { ns: 'users' })}
            name="email"
            rules={[{ type: 'email', message: `Veuillez entrer un ${t('fields.email', { ns: 'users' })} valide` }]}
          >
            <Input onChange={(e) => setNewUserEmail(e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
